/**
 * Constants for the NetworkOrb animation.
 *
 * Lifted verbatim from the design handoff at
 * `design-handoff/components/NetworkOrb.jsx` so that the visual output of this
 * shared component is byte-for-byte identical to the prototype.
 *
 * Lives in a sibling `.constants.ts` file (not in the component file or its
 * `.styled.ts`) so we can avoid the circular-dep runtime crash documented in
 * the project README.
 */

/** Default canvas size in CSS pixels — matches the design prototype. */
export const NETWORK_ORB_DEFAULT_SIZE = 140;

/** Reference size all density / scale factors are calibrated against. */
export const NETWORK_ORB_REFERENCE_SIZE = 140;

/** Cap on the density multiplier so very large orbs don't get particle-overloaded. */
export const NETWORK_ORB_MAX_DENSITY_SCALE = 3;

/** Cap on the per-particle visual scale so dots don't become blobs at huge sizes. */
export const NETWORK_ORB_MAX_PARTICLE_SCALE = 2;

/** Cap on the connection-line scale so the network doesn't fill with edges at huge sizes. */
export const NETWORK_ORB_MAX_LINK_SCALE = 2.5;

/** Bright green used for clickable target dots, click-success state, and shape-morph flash. */
export const NETWORK_ORB_ACCENT_GREEN: readonly [number, number, number] = [118, 185, 0];

/** Particle palette used in dark mode. */
export const NETWORK_ORB_PALETTE_DARK: ReadonlyArray<readonly [number, number, number]> = [
  [90, 140, 255],
  [110, 160, 255],
  [70, 120, 235],
  [130, 170, 255],
];

/** Particle palette used in light mode. */
export const NETWORK_ORB_PALETTE_LIGHT: ReadonlyArray<readonly [number, number, number]> = [
  [50, 100, 220],
  [70, 120, 230],
  [40, 90, 200],
  [80, 130, 240],
];

/** Shape sequence used for the morph animation when all green dots are clicked. */
export const NETWORK_ORB_SHAPES = ['sphere', 'flower', 'cube', 'star', 'smile'] as const;
export type NetworkOrbShape = (typeof NETWORK_ORB_SHAPES)[number];
